import { useState, useEffect } from 'react';

// Material-UI
import {
    TableContainer,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell as _TableCell,
} from '@mui/material';
import { Card, CardHeader, CardContent } from '@mui/material';
import { styled, CircularProgress, Typography, Box, Button } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

// Utility
import fetchExpresso from 'utility/fetchExpresso';
import moment from 'moment-timezone';
import { getRelativeTime } from 'utility/dates';

export default function EmailStatus() {
    const [state, setState] = useState({
        data: null,
        loading: true,
        error: false,
    });

    const { data, loading, error } = state;

    useEffect(() => {
        const tz = moment.tz.guess();

        fetchExpresso(`/admin/reports/email_status?tz=${tz}`)
            .then(async (res) => {
                if (res.status !== 200) {
                    setState((s) => ({ ...s, loading: false, error: true }));
                    return;
                }

                const emailEvents = await res.json();

                let totalEvents = 0;

                for (const event of emailEvents) {
                    totalEvents += event.response_count;
                }

                for (const event of emailEvents) {
                    event.percent = Number(
                        ((event.response_count / totalEvents) * 100).toLocaleString(undefined, {
                            minimumFractionDigits: 1,
                            maximumFractionDigits: 1,
                        }),
                    );
                }

                setState((s) => ({ ...s, data: emailEvents, loading: false, error: false }));
            })
            .catch((e) => {
                setState((s) => ({ ...s, loading: false, error: true }));
            });
    }, []);

    if (loading) {
        return (
            <Root>
                <Status>
                    <CircularProgress disableShrink />
                </Status>
            </Root>
        );
    }

    if (error) {
        return (
            <Root>
                <Status>
                    <Typography>Unable to load stats</Typography>
                </Status>
            </Root>
        );
    }

    return (
        <Root>
            <CardHeader
                title="Emails"
                action={<Button style={{ textDecoration: 'underline' }}>48 Hrs</Button>}
            />

            <CardContent>
                <TableContainer component={Box}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCellBold>EVENT</TableCellBold>
                                <TableCellBold align="right">#</TableCellBold>
                                <TableCellBold align="right">%</TableCellBold>
                                <TableCellBold align="right">
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="flex-end"
                                    >
                                        WHEN
                                        <ArrowDropDownIcon fontSize="small" />
                                    </Box>
                                </TableCellBold>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {data.map((e) => (
                                <TableRow key={e.event}>
                                    <TableCell>{e.event}</TableCell>
                                    <TableCell align="right">{e.response_count}</TableCell>
                                    <TableCell align="right">{e.percent}%</TableCell>
                                    <TableCell width={30} align="right">
                                        {getRelativeTime(e.last_event, 'short')}
                                    </TableCell>
                                </TableRow>
                            ))}

                            {data.length === 0 && (
                                <TableRow>
                                    <TableCell align="center" colSpan={5}>
                                        <Typography variant="subtitle2">No data found</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </CardContent>
        </Root>
    );
}

const Root = styled(Card)({
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '100%',
});

const Status = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
});

const TableCellBold = styled(_TableCell)(({ theme }) => ({
    fontWeight: 'bold',
}));

const TableCell = styled(_TableCell)(({ theme }) => ({
    whiteSpace: 'nowrap',
}));
